/**
 * Hareket türüne göre hangi alanın anlamlı olduğunun TEK tanımı. Form bu
 * tabloya göre alan gösterir/gizler, doğrulama da buradan okur.
 *
 * --- SÖZLEŞME BOŞLUĞU (bilinçli çözüm) ---------------------------------
 * `Transaction`'da `amount` alanı YOK; yalnızca `quantity` ve `price` var.
 * Nakit hareketleri (DEPOSIT, WITHDRAWAL, FEE, INTEREST, DIVIDEND) için
 * arayüz tek bir "Tutar" alanı gösterir ve şu dönüşümü uygular:
 *
 *     quantity = "1",  price = <tutar>
 *
 * Böylece "brüt = quantity × price" değişmezi TÜM türlerde geçerli kalır ve
 * Aşama 3 analiz motoru tek kuralla okur. Ters yön (kayıt → form) aynı
 * dönüşümü tersine uygular.
 *
 * SPLIT ve BONUS'ta da oran alanı yok: `quantity` = eklenen/çıkan MİKTAR
 * FARKI olarak yazılır, `price = "0"`. Oranın kendisi Aşama 3'te şirket
 * olayları tablosundan gelecek.
 * ---------------------------------------------------------------------- */

import type { TransactionType } from "@parafon/contracts";
import { M } from "../dil";

export interface TxFieldShape {
  /** Tek "Tutar" alanı gösterilir; quantity=1, price=tutar olarak yazılır. */
  readonly amount: boolean;
  /** Miktar alanı gösterilir. */
  readonly quantity: boolean;
  /** Birim fiyat alanı gösterilir. */
  readonly price: boolean;
  /** Fiyat zorunlu mu (devirlerde maliyet bilgisi isteğe bağlı). */
  readonly priceRequired: boolean;
  readonly fee: boolean;
  readonly tax: boolean;
  /** Valör alanı. Temettü/faizde kur günü odur (motor: `cashFxDate`). */
  readonly settleDate: boolean;
  readonly fxOverride: boolean;
  /**
   * Varlık alanı: "asset" kullanıcı seçer; "cash" ise nakit varsayılanı
   * kullanılır (elle etiket) ama kullanıcı isterse değiştirebilir.
   */
  readonly assetMode: "asset" | "cash";
  /** Miktar alanının etiketi türe göre değişir. */
  readonly quantityLabel: string;
  /** Kısa açıklama — formun altında görünür. */
  readonly hint: string;
}

const SHARE_TRADE: TxFieldShape = {
  amount: false,
  quantity: true,
  price: true,
  priceRequired: true,
  fee: true,
  tax: true,
  settleDate: true,
  fxOverride: true,
  assetMode: "asset",
  quantityLabel: M.hareketler.sekil.miktar,
  hint: M.hareketler.sekil.alimSatimIpucu,
};

const CASH_MOVE: TxFieldShape = {
  amount: true,
  quantity: false,
  price: false,
  priceRequired: false,
  fee: false,
  tax: false,
  settleDate: false,
  fxOverride: false,
  assetMode: "cash",
  quantityLabel: M.hareketler.sekil.tutar,
  hint: M.hareketler.sekil.nakitIpucu,
};

const QUANTITY_ONLY: TxFieldShape = {
  amount: false,
  quantity: true,
  price: false,
  priceRequired: false,
  fee: false,
  tax: false,
  settleDate: false,
  fxOverride: false,
  assetMode: "asset",
  quantityLabel: M.hareketler.sekil.miktarFarki,
  hint: M.hareketler.sekil.miktarFarkiIpucu,
};

export const TX_SHAPE: Readonly<Record<TransactionType, TxFieldShape>> = {
  BUY: SHARE_TRADE,
  SELL: SHARE_TRADE,
  RIGHTS: {
    ...SHARE_TRADE,
    tax: false,
    quantityLabel: M.hareketler.sekil.alinanMiktar,
    hint: M.hareketler.sekil.ruchanIpucu,
  },
  TRANSFER_IN: {
    ...SHARE_TRADE,
    price: true,
    priceRequired: false,
    tax: false,
    fee: false,
    quantityLabel: M.hareketler.sekil.gelenMiktar,
    hint: M.hareketler.sekil.devirGirisIpucu,
  },
  TRANSFER_OUT: {
    ...SHARE_TRADE,
    price: true,
    priceRequired: false,
    tax: false,
    fee: false,
    quantityLabel: M.hareketler.sekil.cikanMiktar,
    hint: M.hareketler.sekil.devirCikisIpucu,
  },
  SPLIT: { ...QUANTITY_ONLY, hint: M.hareketler.sekil.bolunmeIpucu },
  BONUS: { ...QUANTITY_ONLY, quantityLabel: M.hareketler.sekil.bedelsizMiktar, hint: M.hareketler.sekil.bedelsizIpucu },
  DIVIDEND: {
    ...CASH_MOVE,
    tax: true,
    settleDate: true,
    assetMode: "asset",
    quantityLabel: M.hareketler.sekil.netTutar,
    hint: M.hareketler.sekil.temettuIpucu,
  },
  INTEREST: { ...CASH_MOVE, tax: true, settleDate: true, quantityLabel: M.hareketler.sekil.faizTutari, hint: M.hareketler.sekil.faizIpucu },
  DEPOSIT: { ...CASH_MOVE, quantityLabel: M.hareketler.sekil.yatanTutar, hint: M.hareketler.sekil.yatirmaIpucu },
  WITHDRAWAL: { ...CASH_MOVE, quantityLabel: M.hareketler.sekil.cekilenTutar, hint: M.hareketler.sekil.cekmeIpucu },
  FEE: { ...CASH_MOVE, quantityLabel: M.hareketler.sekil.masrafTutari, hint: M.hareketler.sekil.masrafIpucu },
  TAX_PAYMENT: {
    ...CASH_MOVE,
    quantityLabel: M.hareketler.sekil.odenenTutar,
    hint: M.hareketler.sekil.vergiIpucu,
  },
  FX_CONVERSION: {
    ...CASH_MOVE,
    fee: true,
    quantityLabel: M.hareketler.sekil.verilenTutar,
    hint: M.hareketler.sekil.dovizIpucu,
  },
};

/** Nakit varsayılan etiketi — `AssetBinding` "manual" dalıyla eşleşir. */
export function cashLabel(currency: string): string {
  // dil-disi: kasaya yazılan varlık etiketi; dille değişirse eski nakit satırlarıyla eşleşmez
  return `NAKİT ${currency}`;
}

/** Bu tür miktar mı taşır (pozisyon miktarına katkı verir) yoksa nakit mi? */
export function isQuantityBearing(type: TransactionType): boolean {
  return !TX_SHAPE[type].amount;
}

/** Pozisyon miktarına katkının işareti. Aşama 1: ham toplam, düzeltme yok. */
export function quantitySign(type: TransactionType): -1 | 0 | 1 {
  switch (type) {
    case "BUY":
    case "BONUS":
    case "SPLIT":
    case "RIGHTS":
    case "TRANSFER_IN":
      return 1;
    case "SELL":
    case "TRANSFER_OUT":
      return -1;
    default:
      return 0;
  }
}

/** Nakit hareketlerin nakit bakiyesine katkısının işareti. */
export function cashSign(type: TransactionType): -1 | 0 | 1 {
  switch (type) {
    case "DEPOSIT":
    case "INTEREST":
    case "DIVIDEND":
      return 1;
    case "WITHDRAWAL":
    case "FEE":
      return -1;
    default:
      return 0;
  }
}
